/**
 * PSA ticketing settings.
 *
 * EVERYTHING IS PICKED BY NAME. Asking somebody to type "Queue ID 8" means
 * opening the PSA, finding the queue, and reading a number out of a URL - and a
 * typo does not error, it files tickets somewhere nobody looks. The lists come
 * from the PSA itself.
 *
 * It also refuses to switch on until the PSA's REQUIRED fields are set. Both
 * vendors reject a create that omits them, with messages naming an internal
 * field rather than the thing you chose, and discovering that during an outage
 * is the worst possible time.
 *
 * Which customer a ticket belongs to is not set here - that is the Client
 * mapping under Review.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { errText } from './btApi';
import { TicketProfiles } from './components/TicketProfiles';
import { TicketingExceptions } from './components/TicketingExceptions';
import { OrgMapper } from './orgmap';

function call<TIn extends object, TOut>(name: string) {
  return async (data: TIn): Promise<TOut> => (await httpsCallable<TIn, TOut>(functions, name)(data)).data;
}

type PsaVendor = 'autotask' | 'connectwise';
interface PsaRef { id: string; name: string }
interface PsaOption { id: string; name: string; hint?: string }

interface PsaSettings {
  enabled: boolean;
  vendor: PsaVendor | null;
  defaultCompany: PsaRef | null;
  queue: PsaRef | null;
  status: PsaRef | null;
  priority: PsaRef | null;
  ticketType: PsaRef | null;
  closeStatus: PsaRef | null;
  closeOnRecovery: boolean;
  grouping: 'device' | 'client';
  companyTypeIds: string[];
  companyClassificationIds: string[];
  includeUnclassified: boolean;
}

interface Metadata {
  companies: PsaOption[]; queues: PsaOption[]; statuses: PsaOption[];
  priorities: PsaOption[]; ticketTypes: PsaOption[];
  companyTypes: PsaOption[]; companyClassifications: PsaOption[];
  errors: string[];
}

type VendorStatus = Record<string, { configured: boolean; verified: boolean }>;

const getSettings = call<{ orgId: string },
  { settings: PsaSettings; vendors: VendorStatus; missing: string[] }>('getPsaSettings');
const saveSettings = call<{ orgId: string; settings: PsaSettings }, { ok: boolean }>('setPsaSettings');
const getMetadata = call<{
  orgId: string; vendor: string; boardId?: string; refresh?: boolean;
  filter?: { typeIds?: string[]; classificationIds?: string[]; includeUnclassified?: boolean };
}, Metadata & { cached?: boolean; fetchedAt?: number }>('getPsaMetadata');

/**
 * A multi-select over one filter dimension.
 *
 * Checkboxes rather than a multi-select box: with nothing ticked the meaning is
 * "all", and a native multi-select cannot express that difference legibly.
 */
function FilterList({ label, options, selected, onChange, empty }: {
  label: string;
  options: PsaOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
  empty: string;
}) {
  if (options.length === 0) return null;
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div style={{ minWidth: 220, flex: 1 }}>
      <div className="flbl">{label}</div>
      <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6, padding: 6 }}>
        {options.map((o) => (
          <label className="check-row" key={o.id} style={{ margin: '2px 0', fontSize: 12 }}>
            <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} />
            <span>{o.name}</span>
          </label>
        ))}
      </div>
      <div className="dim" style={{ fontSize: 11, marginTop: 2 }}>
        {selected.length === 0 ? empty : `${selected.length} selected`}
      </div>
    </div>
  );
}
const testTicket = call<{ orgId: string }, { ok: boolean; ticketId: string | null; error: string | null }>('sendTestTicket');

const LABEL: Record<PsaVendor, string> = { autotask: 'Autotask PSA', connectwise: 'ConnectWise Manage' };

/** A name picker over a PSA list, storing {id, name} so the id is never shown. */
function Picker({ label, value, options, onChange, disabled, help }: {
  label: string;
  value: PsaRef | null;
  options: PsaOption[];
  onChange: (v: PsaRef | null) => void;
  disabled?: boolean;
  help?: string;
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      <select
        className="field-input" value={value?.id ?? ''} disabled={disabled}
        onChange={(e) => {
          const o = options.find((x) => x.id === e.target.value);
          onChange(o ? { id: o.id, name: o.name } : null);
        }}
      >
        <option value="">{options.length === 0 ? 'not loaded yet' : 'choose'}</option>
        {/*
          The SAVED value, even before the list arrives. Settings store
          {id, name} precisely so the screen can report what is configured
          without a round trip - this previously showed "load the lists first"
          over a perfectly good saved choice, which is a settings page unable
          to tell you its own settings.
        */}
        {value && !options.some((o) => o.id === value.id) && (
          <option value={value.id}>{value.name || value.id}</option>
        )}
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.name}{o.hint ? ` (${o.hint})` : ''}</option>
        ))}
      </select>
      {help && <span className="dim" style={{ fontSize: 11 }}>{help}</span>}
    </label>
  );
}

export function PsaPanel({ orgId }: { orgId: string }) {
  const [s, setS] = useState<PsaSettings | null>(null);
  const [vendors, setVendors] = useState<VendorStatus>({});
  const [missing, setMissing] = useState<string[]>([]);
  const [meta, setMeta] = useState<Metadata | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [mapping, setMapping] = useState(false);

  const load = async () => {
    try {
      const r = await getSettings({ orgId });
      setS(r.settings);
      setVendors(r.vendors);
      setMissing(r.missing);
      return r.settings;
    } catch (e) { setErr(errText(e)); return null; }
  };

  useEffect(() => {
    let live = true;
    (async () => {
      const saved = await load();
      // Pull the cached lists straight away. The cache makes this cheap, and
      // without it the page opens unable to show what it already knows.
      if (!live || !saved?.vendor) return;
      try {
        const m = await getMetadata({ orgId, vendor: saved.vendor });
        if (!live) return;
        setMeta(m);
        setFetchedAt(m.fetchedAt ?? null);
      } catch { /* not configured, or never loaded - the button is there */ }
    })();
    return () => { live = false; };
  }, [orgId]);

  if (!s) return null;

  const chosen = s.vendor;
  const state = chosen ? vendors[chosen] : undefined;
  const ready = state?.configured === true && state?.verified === true;
  // Typed explicitly: an inline object literal infers never[] for every array,
  // so the union with Metadata loses the fields added later.
  const opts: Metadata = meta ?? {
    companies: [], queues: [], statuses: [], priorities: [], ticketTypes: [],
    companyTypes: [], companyClassifications: [], errors: [],
  };

  /**
   * @param refresh re-read the SAVED settings afterwards. Off for anything
   *        read-only.
   *
   * Loading the PSA lists used to refresh, which re-read the saved settings and
   * threw away the vendor you had just picked but not yet saved - the dropdown
   * snapped back to "none" and every picker collapsed the moment the lists
   * arrived. Exactly the bug already fixed once in the integrations panel,
   * repeated in a new one: a read-only action must not reload state the user is
   * still editing.
   */
  async function run(key: string, fn: () => Promise<string>, refresh = true) {
    setBusy(key); setErr(null); setMsg(null);
    try { setMsg(await fn()); if (refresh) await load(); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(null); }
  }

  return (
    <>
      <section className="panel" style={{ marginTop: '1rem' }}>
      <h3 className="panel-title">PSA ticketing</h3>
      <p className="panel-note" style={{ marginTop: 0 }}>
        Opens a ticket when a machine starts failing and closes it when the machine recovers.
        One ticket per outage, not one per sync.
      </p>

      {err && <div className="banner banner-error">{err}</div>}
      {msg && <div className="banner banner-ok">{msg}</div>}

      <div className="row-form">
        <label className="field">
          <span className="field-label">Send tickets to</span>
          <select
            className="field-input" value={s.vendor ?? ''}
            onChange={(e) => { setS({ ...s, vendor: (e.target.value || null) as PsaVendor | null }); setMeta(null); }}
          >
            <option value="">— none —</option>
            <option value="autotask">Autotask PSA</option>
            <option value="connectwise">ConnectWise Manage</option>
          </select>
        </label>

        <button
          className="btn" style={{ alignSelf: 'flex-end' }}
          disabled={busy !== null || !chosen || state?.configured !== true}
          title={state?.configured ? 'Read the companies, queues and picklists from your PSA' : 'Add the PSA credentials first'}
          onClick={() => run('meta', async () => {
            const m = await getMetadata({
              orgId, vendor: chosen!, boardId: s.queue?.id, refresh: true,
              // The filter being EDITED, not the saved one - previewing an
              // unsaved change is the entire point.
              filter: {
                typeIds: s.companyTypeIds,
                classificationIds: s.companyClassificationIds,
                includeUnclassified: s.includeUnclassified,
              },
            });
            setMeta(m);
            setFetchedAt(m.fetchedAt ?? Date.now());
            return m.errors.length
              ? `Loaded, with problems: ${m.errors.join('; ')}`
              : `Loaded ${m.companies.length} ${s.companyTypeIds.length ? 'matching' : 'active'} ${
                  m.companies.length === 1 ? 'company' : 'companies'
                } and ${m.queues.length} ${chosen === 'connectwise' ? 'boards' : 'queues'}.`;
          }, false)}
        >
          {busy === 'meta' ? 'Loading' : meta ? 'Refresh lists' : 'Load lists from PSA'}
        </button>
        {fetchedAt && (
          <span className="dim" style={{ fontSize: 11, alignSelf: 'flex-end', paddingBottom: 8 }}>
            lists read {new Date(fetchedAt).toLocaleDateString()}
          </span>
        )}

      </div>

      {chosen && (
        <>
          {/*
            WHICH COMPANIES ARE OFFERED is the customer's decision, not a
            constant in our code. This was hardcoded to "type = Customer",
            which is wrong for any install that classifies differently - and
            not ours to decide. Tick nothing to offer everything, then use the
            count above to check before saving.
          */}
          {(opts.companyTypes.length > 0 || opts.companyClassifications.length > 0) && (
            <>
              <div className="flbl" style={{ marginTop: 12 }}>Which companies to offer</div>
              <div className="row-form" style={{ alignItems: 'flex-start' }}>
                <FilterList
                  label="Company type" options={opts.companyTypes}
                  selected={s.companyTypeIds}
                  onChange={(ids) => setS({ ...s, companyTypeIds: ids })}
                  empty="all types"
                />
                <FilterList
                  label="Classification" options={opts.companyClassifications}
                  selected={s.companyClassificationIds}
                  onChange={(ids) => setS({ ...s, companyClassificationIds: ids })}
                  empty="all classifications"
                />
              </div>
              {opts.companyClassifications.length > 0 && (
                <label className="check-row">
                  <input
                    type="checkbox" checked={s.includeUnclassified}
                    onChange={(e) => setS({ ...s, includeUnclassified: e.target.checked })}
                  />
                  <span>Include companies with no classification set</span>
                </label>
              )}
              <p className="panel-note" style={{ marginTop: 4 }}>
                Change these and load the lists again to see what you would get. Nothing is saved
                until you press Save.
              </p>
            </>
          )}

          <div className="flbl" style={{ marginTop: 12 }}>Where tickets go</div>
          <div className="row-form">
            <Picker
              label="Default company" value={s.defaultCompany} options={opts.companies}
              disabled={busy !== null}
              onChange={(v) => setS({ ...s, defaultCompany: v })}
              help="used when a client is not matched yet"
            />
            <Picker
              label={chosen === 'connectwise' ? 'Board' : 'Queue'} value={s.queue} options={opts.queues}
              disabled={busy !== null}
              onChange={(v) => setS({ ...s, queue: v })}
            />
          </div>

          <div className="flbl" style={{ marginTop: 12 }}>
            Required on every ticket
          </div>
          <div className="row-form">
            <Picker
              label="Status for new tickets" value={s.status} options={opts.statuses}
              disabled={busy !== null} onChange={(v) => setS({ ...s, status: v })}
            />
            <Picker
              label="Priority" value={s.priority} options={opts.priorities}
              disabled={busy !== null} onChange={(v) => setS({ ...s, priority: v })}
            />
            {chosen === 'autotask' && (
              <Picker
                label="Ticket type" value={s.ticketType} options={opts.ticketTypes}
                disabled={busy !== null} onChange={(v) => setS({ ...s, ticketType: v })}
              />
            )}
            <Picker
              label="Status when it recovers" value={s.closeStatus} options={opts.statuses}
              disabled={busy !== null} onChange={(v) => setS({ ...s, closeStatus: v })}
              help="leave blank for the vendor default"
            />
          </div>
        </>
      )}

      <div className="field" style={{ marginTop: 14, maxWidth: 560 }}>
        <span className="field-label">How many tickets one outage produces</span>
        <select
          className="field-input" style={{ maxWidth: 320 }} disabled={busy !== null}
          value={s.grouping ?? 'device'}
          onChange={(e) => setS({ ...s, grouping: e.target.value === 'client' ? 'client' : 'device' })}
        >
          <option value="device">One ticket per machine</option>
          <option value="client">One ticket per client</option>
        </select>
        <span className="field-hint">
          A client whose appliance dies takes nine machines with it, and one ticket per machine turns
          that into nine tickets for one visit. One per client collapses it - the ticket stays open
          until the <em>last</em> of that client&rsquo;s machines recovers, so an early recovery cannot
          close a ticket that still covers seven broken ones. Individual clients can be set
          differently under Policies.
        </span>
      </div>

      <label className="check-row">
        <input
          type="checkbox" checked={s.closeOnRecovery}
          onChange={(e) => setS({ ...s, closeOnRecovery: e.target.checked })}
        />
        <span>Close the ticket automatically when the machine backs up again</span>
      </label>

      <label className="check-row">
        <input
          type="checkbox" checked={s.enabled} disabled={!ready}
          onChange={(e) => setS({ ...s, enabled: e.target.checked })}
        />
        <span>
          Enabled — create real tickets
          {chosen && !state?.configured && <span className="dim"> — {LABEL[chosen]} has no credentials yet</span>}
          {chosen && state?.configured && !state?.verified && (
            <span className="dim"> - send a test ticket first, to prove {LABEL[chosen]} accepts them</span>
          )}
        </span>
      </label>

      {missing.length > 0 && s.enabled && (
        <div className="banner banner-error">
          {chosen ? LABEL[chosen] : 'Your PSA'} will reject tickets without {missing.join(', ')}.
        </div>
      )}

      {s.enabled && (
        <p className="panel-note">
          Machines that are <strong>already failing</strong> will not raise a ticket. Tickets are
          raised when a machine <em>changes</em> to failing, so nothing is filed for problems that
          were already on the board when you switched this on.
        </p>
      )}

      <div className="row-form" style={{ marginTop: '.6rem' }}>
        <button
          className="btn btn-primary" disabled={busy !== null}
          onClick={() => run('save', async () => {
            await saveSettings({ orgId, settings: s });
            return s.enabled ? 'Saved. Tickets will be raised on the next failure.' : 'Saved.';
          })}
        >
          {busy === 'save' ? 'Saving…' : 'Save'}
        </button>

        <button
          className="btn" disabled={busy !== null || !ready}
          title={ready ? 'Opens one real ticket, clearly marked as a test' : 'Configure and verify the PSA first'}
          onClick={() => run('test', async () => {
            const r = await testTicket({ orgId });
            if (!r.ok) throw new Error(r.error ?? 'The ticket was not created.');
            return `Test ticket created${r.ticketId ? ` (#${r.ticketId})` : ''}. Close it in your PSA.`;
          }, false)}
        >
          {busy === 'test' ? 'Sending' : 'Send a test ticket'}
        </button>

        <button
          className="btn"
          disabled={busy !== null || opts.companies.length === 0}
          title={opts.companies.length ? 'Match imported organisations to PSA companies'
                                       : 'Load the PSA lists first'}
          onClick={() => setMapping(!mapping)}
        >
          {mapping ? 'Hide mapping' : 'Map organisations'}
        </button>
      </div>

      {mapping && opts.companies.length > 0 && (
        <OrgMapper orgId={orgId} companies={opts.companies} onClose={() => setMapping(false)} />
      )}

      <p className="panel-note">
        Which customer a ticket is filed against comes from the mapping above. New organisations
        that appear later are raised under
        <strong> Review</strong>. Unmatched clients fall back to the default company above, with the
        client name in the ticket title.
      </p>
      </section>

      {/* Directly below the defaults it inherits from, so "blank means the
          setting above" is something you can see rather than remember. */}
      <TicketProfiles orgId={orgId} canManage />

      {/* Last, because it is a verdict ON everything above it. Placed first it
          would be a check somebody runs before configuring anything. */}
      <TicketingExceptions orgId={orgId} />
    </>
  );
}
